import React, { Fragment, useMemo, useContext, memo } from 'react'
import injectSheet from 'react-jss'
import styles, {
  formRowStyles,
  formHeadStyles,
  formControlsStyles,
  buttonStyles
} from './sc-form.styles'
import { Formik, FieldArray } from 'formik'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlusCircle } from '@fortawesome/pro-solid-svg-icons/faPlusCircle'
import { faTrashAlt } from '@fortawesome/pro-solid-svg-icons/faTrashAlt'
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck'
import { faCog } from '@fortawesome/pro-solid-svg-icons/faCog'
import { faEye } from '@fortawesome/pro-solid-svg-icons/faEye'
import { faEyeSlash } from '@fortawesome/pro-solid-svg-icons/faEyeSlash'
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes'
import { SCContext } from '../sc-helpers'
import { withPresentation } from '../../../../presentation-connectors'
import {
  SortableHandle,
  SortableContainer,
  SortableElement
} from 'react-sortable-hoc'
import LineBreak from 'pobble-components/dist/line-break'
import TextareaAutosize from 'react-autosize-textarea'
import classNames from 'classnames'

/**************************
 * Button
 */
const Button = withPresentation(
  injectSheet(buttonStyles)(
    ({ classes, children, icon, className, type = 'button', onClick }) => (
      <button
        type={type}
        className={classNames(classes.button, className)}
        onClick={onClick}>
        <span className={classes.iconHolder}>{icon}</span>
        <span className={classes.title}>{children}</span>
      </button>
    )
  )
)

/**************************
 * Form controls
 */
const FormControls = withPresentation(
  injectSheet(formControlsStyles)(
    ({ classes, onSave, readOnly, resetForm }) => {
      const {
        setEditMode,
        editMode,
        showPrompts,
        setShowPrompts,
        updatingData
      } = useContext(SCContext)
      return (
        <div className={classes.formContorls}>
          {!readOnly && (
            <Fragment>
              {!editMode && (
                <Button
                  onClick={() => setEditMode(true)}
                  className={classes.button}
                  icon={<FontAwesomeIcon icon={faCog} />}>
                  Edit
                </Button>
              )}
              {editMode && !updatingData && (
                <Fragment>
                  <Button
                    type="submit"
                    onClick={onSave}
                    className={classNames(classes.button, classes.saveButton)}
                    icon={<FontAwesomeIcon icon={faCheck} />}>
                    Save
                  </Button>
                  <Button
                    onClick={() => {
                      resetForm()
                      setEditMode(false)
                    }}
                    className={classNames(classes.button, classes.cancelButton)}
                    icon={<FontAwesomeIcon icon={faTimes} />}>
                    Cancel
                  </Button>
                </Fragment>
              )}
              {updatingData && (
                <span className={classes.pendingNote}>Saving...</span>
              )}
              <div className={classes.separator} />
            </Fragment>
          )}

          <Button
            onClick={() => setShowPrompts(!showPrompts)}
            className={classNames(classes.button, classes.promptsButton)}
            icon={
              <FontAwesomeIcon
                style={{ width: '1.25em' }}
                icon={showPrompts ? faEye : faEyeSlash}
              />
            }>
            Prompts
          </Button>
        </div>
      )
    }
  )
)

/**************************
 * Form head
 */
const FormHead = withPresentation(
  injectSheet(formHeadStyles)(({ classes }) => (
    <div className={classes.formHead}>
      <div className={classNames(classes.cell, classes.firstCell)} />
      <div className={classNames(classes.cell, classes.lastCell)}>
        <FontAwesomeIcon icon={faCheck} />
      </div>
    </div>
  ))
)

/**************************
 * Form row
 */
const FormRow = withPresentation(
  injectSheet(formRowStyles)(
    ({ classes, criterion, prompts, formikProps, onDelete, id }) => {
      const { handleChange } = formikProps
      const { showPrompts, editMode } = useContext(SCContext)
      return (
        <div className={classes.formRow}>
          <div className={classNames(classes.cell, classes.firstCell)}>
            {editMode && (
              <div className={classes.sortCell}>
                <SortHandle className={classes.sortHandle} />
              </div>
            )}
            <div className={classes.text}>
              {editMode && (
                <TextareaAutosize
                  placeholder="Criterion"
                  name={`criteria.${id}.criterion`}
                  onChange={handleChange}
                  value={criterion}
                  className={classNames(classes.textArea, classes.bold)}
                />
              )}
              {!editMode && (
                <p className={classes.criterion}>
                  <LineBreak>{criterion}</LineBreak>
                </p>
              )}
              {showPrompts && (
                <Fragment>
                  {!editMode && !!prompts && (
                    <p className={classes.prompts}>
                      <LineBreak>{prompts}</LineBreak>
                    </p>
                  )}
                  {editMode && (
                    <TextareaAutosize
                      placeholder="Prompts"
                      name={`criteria.${id}.prompts`}
                      onChange={handleChange}
                      value={prompts}
                      className={classes.textArea}
                    />
                  )}
                </Fragment>
              )}
            </div>
            {editMode && (
              <div className={classes.deleteCell}>
                <FontAwesomeIcon
                  onClick={onDelete}
                  className={classes.deleteButton}
                  icon={faTrashAlt}
                />
              </div>
            )}
          </div>
          <div className={classNames(classes.cell, classes.lastCell)} />
        </div>
      )
    }
  )
)

/**************************
 * Sortable elements
 */
const SortHandleIcon = props => (
  <svg width={18} height={22} viewBox="0 0 18 22" {...props}>
    <g fill="currentColor" fillRule="evenodd">
      <path d="M9.5 0L7.75 2 6 4h7l-1.75-2zM9.5 18H6l1.75 2 1.75 2 1.75-2L13 18zM18 15.278c0 .28-.172.506-.384.506H.384c-.212 0-.384-.227-.384-.506v-.772c0-.28.172-.506.384-.506h17.232c.212 0 .384.226.384.506v.772zM18 11.278c0 .28-.172.506-.384.506H.384c-.212 0-.384-.227-.384-.506v-.772c0-.28.172-.506.384-.506h17.232c.212 0 .384.226.384.506v.772zM18 7.278c0 .28-.172.506-.384.506H.384C.172 7.784 0 7.557 0 7.278v-.772C0 6.226.172 6 .384 6h17.232c.212 0 .384.226.384.506v.772z" />
    </g>
  </svg>
)

const SortHandle = SortableHandle(props => <SortHandleIcon {...props} />)
const SortableHolder = SortableContainer(({ componentRef, ...props }) => (
  <div ref={componentRef} {...props} />
))

const SortableFormRow = SortableElement(props => <FormRow {...props} />)

/**************************
 * SC Form
 */
const SCForm = memo(({ classes, slides, id }) => {
  const data = slides[id]

  const { editMode, onSubmit } = useContext(SCContext)

  const slideData = useMemo(
    () => ({
      criteria: data.criteria
    }),
    [data]
  )

  return (
    <Formik
      enableReinitialize
      onSubmit={onSubmit}
      initialValues={{
        criteria: slideData.criteria
      }}>
      {formikProps => {
        const { values, resetForm, handleSubmit } = formikProps
        return (
          <form onSubmit={handleSubmit} className={classes.container}>
            <FieldArray name="criteria">
              {arrayHelpers => (
                <Fragment>
                  <FormControls resetForm={resetForm} />
                  <div className={classes.formHolder}>
                    <FormHead />
                    <div className={classes.entriesHolder}>
                      <div className={classes.fader} />
                      <SortableHolder
                        onSortEnd={({ oldIndex, newIndex }) =>
                          arrayHelpers.move(oldIndex, newIndex)
                        }
                        useDragHandle={true}
                        helperClass={classes.sortHelper}
                        className={classes.entriesContainer}>
                        {values.criteria.map(
                          ({ criterion, prompts }, index) => (
                            <SortableFormRow
                              onDelete={() => arrayHelpers.remove(index)}
                              id={index}
                              index={index}
                              formikProps={formikProps}
                              key={index}
                              criterion={criterion}
                              prompts={prompts}
                            />
                          )
                        )}
                        {editMode && (
                          <Button
                            onClick={() =>
                              arrayHelpers.push({ criterion: '', prompts: '' })
                            }
                            className={classes.addLineButton}
                            icon={<FontAwesomeIcon icon={faPlusCircle} />}>
                            Add a line
                          </Button>
                        )}
                      </SortableHolder>
                    </div>
                  </div>
                </Fragment>
              )}
            </FieldArray>
          </form>
        )
      }}
    </Formik>
  )
})

export default withPresentation(injectSheet(styles)(SCForm))
